/**
 * Utility functions for managing Google Fonts in Builder UI frames
 */

export interface GoogleFont {
  family: string;
  weights?: Set<string>;
}

/**
 * Builds a weight range string for Google Fonts API
 * @param variants Array of font weight variants
 * @returns Weight range string or empty string if no valid weights
 */
export const buildWeightRange = (variants: string[]): string => {
  const weights = variants
    .filter(v => v.trim().toLowerCase() !== "regular")
    .map(v => parseInt(v, 10))
    .filter(weight => !isNaN(weight) && weight > 0)
    .sort((a, b) => a - b);

  if (weights.length === 0) return "";

  if (weights.length === 1) {
    return weights[0].toString();
  }

  if (consecutiveWeights(weights)) {
    return `${weights[0]}..${weights[weights.length - 1]}`;
  }

  return weights.join(";");
};

/**
 * Checks if an array of weights are consecutive
 * @param weights Sorted array of font weights
 * @returns True if all weights are consecutive
 */
const consecutiveWeights = (weights: number[]): boolean =>
  weights.every(
    (weight, index) => index === 0 || weight === weights[index - 1] + 1,
  );

/**
 * Generates Google Fonts link tags for the given fonts
 * @param fonts Map of Google Fonts
 * @returns HTML string with link tags for Google Fonts
 */
export const generateGoogleFontsLinkTags = (
  fonts: Map<string, GoogleFont>,
): string => {
  if (!fonts || fonts.size === 0) return "";

  const fontFamilies = Array.from(fonts.values()).map(font => {
    const familyParam = font.family.replace(/\s+/g, "+");
    if (font.weights && font.weights.size > 0) {
      const weightRange = buildWeightRange(Array.from(font.weights));
      if (weightRange) {
        return `${familyParam}:wght@${weightRange}`;
      }
      return familyParam;
    }
    return familyParam;
  });

  if (fontFamilies.length === 0) return "";

  const googleFontsUrl = `https://fonts.googleapis.com/css2?family=${fontFamilies.join("&family=")}&display=swap`;

  return `<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${googleFontsUrl}" rel="stylesheet">`;
};
